import { useMemo } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { SelectSize } from '@lyearn/ui';
import AsyncSelect from '@/components/AsyncSelect';
import { Maybe } from '@/types/schema';
import { timezones } from '@/utils/timezone/timezones';

import TimezoneSelectorMobile from './TimezoneSelectorMobile';

type Timezone = (typeof timezones)[0];

const useTimezoneQuery = ({ searchText }: { searchText: string }) => {
  const data = useMemo(() => {
    if (!searchText) {
      return timezones;
    }

    return timezones.filter((timezone) =>
      timezone.name.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [searchText]);

  return { data };
};

interface Props {
  placeholder?: string;
  className?: string;
  size?: SelectSize;
  value?: Maybe<string>;
  disabled?: boolean;
  onChange: (value: string) => void;
}

const isOptionEqualToValue = (option: Timezone, value: Timezone) => option.tzCode === value.tzCode;

const TimezoneSelector = (props: Props) => {
  const { t } = useTranslation();
  const { placeholder, className, size, value, onChange: onChangeProp, disabled } = props;
  const { isMobile } = useScreen();
  const selectedOption = useMemo(() => {
    if (!value) {
      return null;
    }

    return timezones.find((timezone) => timezone.tzCode === value) ?? null;
  }, [value]);

  const onChange = useEvent((option: Timezone) => {
    onChangeProp(option.tzCode);
  });

  const mobileValue = value ?? '';

  return isMobile ? (
    <TimezoneSelectorMobile
      setFieldValue={(field, val, shouldValidate) => onChange({ tzCode: val, name: '', utc: '' })}
      value={mobileValue}
    />
  ) : (
    <AsyncSelect
      disableClearable
      className={className}
      disabled={disabled}
      isOptionEqualToValue={isOptionEqualToValue}
      placeholder={placeholder ?? t('Common:Actions.Select')}
      size={size}
      useOptionsQuery={useTimezoneQuery}
      value={selectedOption as TSFixMe}
      onChange={onChange}
    />
  );
};

export default TimezoneSelector;
